import { action, observable, makeObservable, runInAction } from 'mobx'
import { types as t, flow } from '@invisr/mobx-state-tree'
import crudActions from './crud-actions'
import Mapping, { IMappingSnapshot } from './mapping'
import fetch from '../../utils/fetch'
import { v4 as uuid } from 'uuid'
import { fetchEventSource } from '@microsoft/fetch-event-source';

class FatalError extends Error {}

const AIConfig = t
  .model('FN', {
    type: t.optional(t.string, 'AI'),
  })
  .views(self => ({
    get provider () {
      return AIProvider
    },
    get icon () {
      return 'code'
    },
    labels () {
      return {
        key: (self as any).id as string,
        label: (self as any).name as string,
        childNodes: [
          {
            key: 'methods',
            label: 'Methods',
            childNodes: [
              {
                key: 'text',
                label: 'text',
                secondaryLabel: 'FUNCTION'
              },
              {
                key: 'image',
                label: 'image',
                secondaryLabel: 'FUNCTION'
              },
              {
                key: 'chat',
                label: 'chat',
                secondaryLabel: 'FUNCTION'
              },
              {
                key: 'cancel',
                label: 'cancel',
                secondaryLabel: 'FUNCTION'
              }
            ]
          }
        ]
      }        
    }
  }))
  .actions(crudActions)

export class AIProvider {
  config = null
  data = {
    chat: {},
    results: [],
    queryResult: null
  }
  isLoading = false
  abortCtrl = null

  constructor (config) {
    makeObservable(this, {
      isLoading: observable,
      setIsLoading: action,
      data: observable,
      addChatMessage: action,
      updateLastMessage: action,
    })

    this.config = config
  }

  setIsLoading = (b = true) => {
    this.isLoading = b
  }

  cancel = () => {
    this.abortCtrl?.abort()
    this.abortCtrl = null
  }

  image = async (params = {}) => {
    this.setIsLoading()
    try {
      const res = await fetch(`/api/datasource/${this.config.dbKey}/image`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(params)
      })
      
      const resp = await res.json()
      if (res.status !== 200) {
        console.error(resp)
        throw new Error(resp.errorMessage)
      }

      return resp
    } finally {
      this.setIsLoading(false)
    }
  }

  text = async (params = {}) => {
    this.setIsLoading()
    try {
      const res = await fetch(`/api/datasource/${this.config.dbKey}/text`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(params)
      })
      
      const resp = await res.json()
      if (res.status !== 200) {
        console.error(resp)
        throw new Error(resp.errorMessage)
      }

      return resp
    } finally {
      this.setIsLoading(false)
    }
  }

  _chat = async (message, chatSessionId, model = 'gpt-3.5-turbo', stream = false, endpoint = 'chat') => {
    if(!chatSessionId) chatSessionId = uuid()
    this.setIsLoading()
    try {
      const ctrl = new AbortController();
      this.abortCtrl = ctrl
      const body = JSON.stringify({ prompt: message, chatSessionId, model, stream: !!stream })
      const headers = { 'Content-Type': 'application/json' }
      const method = 'POST'
      this.addChatMessage(message, chatSessionId, 'outbound')
      let newMsg
      let result
      await fetchEventSource(`/api/datasource/${this.config.dbKey}/${endpoint}`, {
        method, headers, body,
        onmessage: (e) => {
          if(e.event === 'done') {
            result = JSON.parse(e.data)
            this.cancel()
            return 
          }
          if(e.event === 'error') {
            console.error(e.data)
            this.cancel()
            return
          }
          const { text } = JSON.parse(e.data)
          if(!newMsg) {
            this.addChatMessage(text, chatSessionId, 'inbound')
          } else {
            this.updateLastMessage(text, chatSessionId, 'inbound')
          }
          if(typeof stream === 'function') {
            stream(text)
          }
          newMsg = text
        },
        onerror: (err) => {
          console.error(err)
          this.cancel()
          throw err
        },
        onclose: (e) => {
          console.log('CLOSE', e)
          this.cancel()
        },
        signal: ctrl.signal
      })

      return result

    } finally {
      this.setIsLoading(false)
    }
  }

  chat = async (message, chatSessionId, model = 'gpt-3.5-turbo', stream = false) => {
    return this._chat(message, chatSessionId, model, stream, 'chat')
  }

  query = () => {
    this.setIsLoading(false)
    return this.data
  }

  addChatMessage(message, chatSessionId, direction) {
    this.data.chat[chatSessionId] = this.data.chat[chatSessionId] || []
    this.data.chat[chatSessionId].push({ message, direction, creationTime: +new Date() })
    //this.data.chat = { ...this.data.chat }
  }

  updateLastMessage(message, chatSessionId, direction) {
    this.data.chat[chatSessionId] = this.data.chat[chatSessionId] || []
    let last = this.data.chat[chatSessionId].length - 1
    this.data.chat[chatSessionId][last] = { ...this.data.chat[chatSessionId][last], message}
  }
}

export default AIConfig
